import {TextField} from "@mui/material";
import {ThreeDots} from "@dvrt/graphql-widgets";
import BackendError from "../BackendError";
import {Page} from "../pages";
import {FormActionButtons, FormattedFormRow, FormContainer, FormRow} from "@dvrt/graphql-widgets";
import {useForm} from "@/utils/Form";
import ErrorHelper from "../../utils/ErrorHelper";
import MultipleLineError from "../MultipleLineError";
import {useFormGuard} from "../../utils/formGuard";
import {useGroupNameQuery, useSavePickupGroupMutation} from "./queries.generated";
import {defineMessage} from "react-intl";

type Props = {
    id: number;
};

// This component allows the user to update the name of a pickup group.
const UpdatePickupGroup = ({id}: Props) => {
    const form = useForm(useSavePickupGroupMutation, "savePickupGroup", {
        id,
        name: "",
    });
    const errorHelper = new ErrorHelper(form.errors);
    const query = useGroupNameQuery({
        variables: {id},
        onCompleted: (data) => {
            form.newInitialData({
                id: id,
                name: data.groups.items[0].name,
            });
        },
    });

    useFormGuard(form.hasChanged);

    if (query.loading)
        return (
            <div style={{padding: 20}}>
                <ThreeDots height={5} />
            </div>
        );
    if (query.error)
        return (
            <div style={{padding: 20}}>
                <BackendError error={query.error} />
            </div>
        );

    async function submit() {
        await form.submit({updateInitialData: true});
    }

    return (
        <Page
            title={defineMessage({id: "pickupGroup.title", defaultMessage: 'Settings for pickup group "{name}"'})}
            titleValues={{name: query.data?.groups.items[0].name || ""}}
        >
            <FormContainer style={{maxWidth: 400}}>
                <FormattedFormRow label={defineMessage({id: "name", defaultMessage: "Name"})} required>
                    <TextField
                        variant="standard"
                        required
                        margin="dense"
                        fullWidth
                        value={form.values.name}
                        onChange={(event) => form.updateValues({name: event.target.value})}
                        error={errorHelper.hasError("name")}
                        helperText={<MultipleLineError errors={errorHelper.fieldErrors("name")} />}
                    />
                </FormattedFormRow>
                <FormRow>
                    <FormActionButtons
                        mainAction={submit}
                        secondaryAction={() => {
                            form.reset();
                            form.clearErrors();
                        }}
                        enabled={form.hasChanged}
                    />
                </FormRow>
            </FormContainer>
        </Page>
    );
};

export default UpdatePickupGroup;
